import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Footprints, MapPinned } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACK_KEY = 'work_start_ack';
const POLL_MS = 15000;

const playChime = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === 'suspended') ctx.resume();
    const note = (freq, t0, dur) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, ctx.currentTime + t0);
      g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + t0 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + t0 + dur);
      o.connect(g);
      g.connect(ctx.destination);
      o.start(ctx.currentTime + t0);
      o.stop(ctx.currentTime + t0 + dur + 0.05);
    };
    note(880, 0, 0.35);
    note(1318.51, 0.18, 0.5);
  } catch {
    // audio unavailable/blocked — popup still shows
  }
};

export const WorkStartAlert = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [newStarts, setNewStarts] = useState([]);
  const latestRef = useRef(null);

  const checkStarts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/work-starts/today`, getAuthHeader());
      const { latest_started_at, starts } = res.data || {};
      if (!latest_started_at) return;
      latestRef.current = latest_started_at;
      const acked = localStorage.getItem(ACK_KEY);
      if (!acked || latest_started_at > acked) {
        const fresh = acked ? starts.filter((s) => s.started_at > acked) : starts;
        if (fresh.length > 0) {
          setNewStarts(fresh);
          setOpen(true);
          playChime();
        }
      }
    } catch {
      // silent — polling should never surface errors
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    checkStarts();
    const id = setInterval(checkStarts, POLL_MS);
    return () => clearInterval(id);
  }, [checkStarts]);

  const acknowledge = () => {
    if (latestRef.current) localStorage.setItem(ACK_KEY, latestRef.current);
    setOpen(false);
  };

  const viewLiveMap = () => {
    acknowledge();
    navigate('/admin/live-map');
  };

  const fmtTime = (iso) => {
    if (!iso) return '';
    return new Date(iso.endsWith('Z') ? iso : iso + 'Z').toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) acknowledge(); }}>
      <DialogContent className="max-w-sm" data-testid="work-start-alert-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-emerald-600">
            <Footprints size={20} />
            {newStarts.length === 1
              ? `${newStarts[0].user_name} started working!`
              : `${newStarts.length} workers started working!`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-1.5 max-h-44 overflow-y-auto">
          {newStarts.map((s) => (
            <div key={`${s.user_id}-${s.date_ct}`} className="flex items-center justify-between text-sm bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2"
              data-testid={`work-start-item-${s.user_id}`}>
              <span className="text-gray-800 font-medium truncate">{s.user_name}</span>
              <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                {s.job_title ? `${s.job_title} · ` : ''}{fmtTime(s.started_at)}
              </span>
            </div>
          ))}
        </div>
        <div className="flex gap-3 justify-end mt-2">
          <Button variant="outline" onClick={acknowledge} data-testid="work-start-dismiss-btn">
            Dismiss
          </Button>
          <Button onClick={viewLiveMap} className="bg-emerald-600 hover:bg-emerald-700" data-testid="work-start-view-map-btn">
            <MapPinned size={15} className="mr-1.5" />
            View Live Map
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkStartAlert;
